'use client';

import React from 'react';
import { ArrowRight, Globe, Sparkles, Volume2, Zap } from 'lucide-react';

import { routes } from '@workspace/routes';
import { Button } from '@workspace/ui/components/customButton';

import ctaBg from '~/public/cta-bg.png';
import { BlurFade } from '../fragments/blur-fade';
import CTAMockup from '../mockups/cta-mockup';

import { trackCta } from '~/lib/track-cta';

interface CTASectionProps {
  title?: string;
  description?: string;
  buttonText?: string;
  emoji?: string;
}

const CTASection: React.FC<CTASectionProps> = ({
  title = 'Ready to start creating tutorial videos?',
  description = 'Click on the link below and get started with VideoMule and turn your screen recordings into narrated videos.',
  buttonText = 'Get started',
  emoji
}) => {
  return (
    <section className="py-16 sm:py-20 lg:py-24 relative overflow-hidden">
      {/* Background image */}
      <div
        className="absolute inset-0 bg-cover bg-center bg-no-repeat dark:opacity-30"
        style={{ backgroundImage: `url(${ctaBg.src})` }}
      />
      {/* Dark mode background */}
      <div className="absolute inset-0 bg-background hidden dark:block dark:-z-10" />

      <div className="max-w-7xl mx-auto px-4 sm:px-6 relative z-10">
        <div className="grid lg:grid-cols-2 gap-10 lg:gap-8 items-center">
          {/* Left side - Content */}
          <div className="order-2 lg:order-1">
            {/* Main headline */}
            <BlurFade
              inView
              delay={0.1}
            >
              <h2 className="text-pretty text-5xl font-bold lg:text-6xl mb-6">
                {title}
                <span className="text-green-500">.</span>
                {emoji && <span className="inline-block ml-3">{emoji}</span>}
              </h2>
            </BlurFade>

            {/* Description */}
            <BlurFade
              inView
              delay={0.2}
            >
              <p className="text-base sm:text-lg lg:text-xl text-muted-foreground mb-8 leading-relaxed max-w-lg">
                {description}
              </p>
            </BlurFade>

            {/* CTA Button */}
            <BlurFade
              inView
              delay={0.3}
            >
              <Button
                size="lg"
                href={routes.dashboard.auth.SignUp}
                onClick={() => trackCta('cta_signup_clicked', { surface: 'tool_cta_section', path: window.location.pathname })}
                className="bg-primary hover:bg-primary-hover text-primary-foreground font-semibold text-base px-8 py-6 rounded-full shadow-lg shadow-primary/20 transition-all duration-300 transform hover:scale-105 group"
              >
                {buttonText}
                <ArrowRight className="ml-2 h-5 w-5 group-hover:translate-x-1 transition-transform" />
              </Button>
            </BlurFade>

            {/* Feature pills */}
            <BlurFade
              inView
              delay={0.4}
            >
              <div className="mt-8 flex flex-wrap gap-3">
                <div className="inline-flex items-center gap-2 bg-white/80 dark:bg-card/80 backdrop-blur-sm border border-border/50 rounded-full px-4 py-2 text-sm text-muted-foreground">
                  <Sparkles className="w-4 h-4 text-primary" />
                  AI Powered
                </div>
                <div className="inline-flex items-center gap-2 bg-white/80 dark:bg-card/80 backdrop-blur-sm border border-border/50 rounded-full px-4 py-2 text-sm text-muted-foreground">
                  <Volume2 className="w-4 h-4 text-primary" />
                  100+ Voices
                </div>
                <div className="inline-flex items-center gap-2 bg-white/80 dark:bg-card/80 backdrop-blur-sm border border-border/50 rounded-full px-4 py-2 text-sm text-muted-foreground">
                  <Globe className="w-4 h-4 text-primary" />
                  70+ Languages
                </div>
              </div>
            </BlurFade>
          </div>

          {/* Right side - Dashboard mockup */}
          <BlurFade
            inView
            delay={0.2}
            className="relative order-1 lg:order-2"
          >
            {/* Floating elements */}
            <div
              className="absolute -top-4 -left-2 sm:-top-6 sm:left-0 z-20 bg-white dark:bg-card border border-border/50 rounded-xl px-3 py-2 sm:px-4 sm:py-2.5 shadow-lg"
              style={{ animation: 'float 4s ease-in-out infinite' }}
            >
              <div className="flex items-center gap-2">
                <div className="w-8 h-8 rounded-lg bg-primary/10 flex items-center justify-center">
                  <Sparkles className="w-4 h-4 text-primary" />
                </div>
                <div>
                  <p className="text-xs font-semibold text-foreground">
                    AI Script
                  </p>
                  <p className="text-[10px] text-muted-foreground">
                    Auto-generated
                  </p>
                </div>
              </div>
            </div>

            <div
              className="absolute -bottom-2 -left-2 sm:-bottom-4 sm:left-4 z-20 bg-white dark:bg-card border border-border/50 rounded-xl px-3 py-2 sm:px-4 sm:py-2.5 shadow-lg"
              style={{
                animation: 'float 5s ease-in-out infinite',
                animationDelay: '1s'
              }}
            >
              <div className="flex items-center gap-2">
                <div className="w-8 h-8 rounded-lg bg-orange-100 dark:bg-orange-500/20 flex items-center justify-center">
                  <Volume2 className="w-4 h-4 text-orange-500" />
                </div>
                <div>
                  <p className="text-xs font-semibold text-foreground">
                    Voice Ready
                  </p>
                  <p className="text-[10px] text-muted-foreground">
                    Professional quality
                  </p>
                </div>
              </div>
            </div>

            <div
              className="absolute top-1/3 -right-2 sm:-right-4 z-20 bg-white dark:bg-card border border-border/50 rounded-xl px-3 py-2 sm:px-4 sm:py-2.5 shadow-lg"
              style={{
                animation: 'float 4.5s ease-in-out infinite',
                animationDelay: '0.5s'
              }}
            >
              <div className="flex items-center gap-2">
                <div className="w-8 h-8 rounded-lg bg-green-100 dark:bg-green-500/20 flex items-center justify-center">
                  <Zap className="w-4 h-4 text-green-500" />
                </div>
                <div>
                  <p className="text-xs font-semibold text-foreground">
                    Export
                  </p>
                  <p className="text-[10px] text-muted-foreground">
                    1-click download
                  </p>
                </div>
              </div>
            </div>

            {/* Dashboard mockup */}
            <div className="relative transform lg:scale-105 lg:translate-x-4 xl:scale-110 xl:translate-x-6">
              <CTAMockup />
            </div>

            {/* Glow effect behind */}
            <div className="absolute inset-0 -z-10 bg-gradient-to-r from-primary/15 via-blue-300/10 to-purple-300/10 dark:from-primary/10 dark:via-primary/5 dark:to-primary/5 rounded-3xl blur-3xl transform scale-110" />
          </BlurFade>
        </div>
      </div>
    </section>
  );
};

export default CTASection;
